<template>
  <tg-layout :headerHeight="60">
    <template slot="header">
      <el-row type="flex" align="middle" class="filter-row">
        <!-- 所屬月份 -->
        <label class="filter-label">{{ $t("column.month_belong") }}</label>
        <el-select
          v-model="searchData.month"
          class="filter-select"
          size="small"
          @change="handleSearch"
        >
          <el-option
            v-for="item in monthOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>

        <!-- 活動類別 -->
        <label class="filter-label">{{ $t("column.activity_class") }}</label>
        <el-select
          v-model="searchData.category"
          class="filter-select filter-select--category"
          size="small"
          @change="handleSearch"
        >
          <el-option
            v-for="item in categoryOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>

        <!-- 排序 -->
        <label class="filter-label">{{ $t("column.sort") }}</label>
        <el-select
          v-model="searchData.sort"
          class="filter-select filter-select--wide"
          size="small"
          @change="handleSearch"
        >
          <el-option
            v-for="item in sortOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </el-row>
    </template>

    <TgTable
      ref="table"
      :columns="columns"
      :proxy="proxy"
      :formatter="formatCell"
      align="center"
      :fit="true"
      showSummary
      :summary-method="parseSummary"
      @on-loaded="handleLoaded"
    ></TgTable>
  </tg-layout>
</template>

<script>
import TgLayout from "@/layout/TgLayout";
import TgTable from "@/components/table/TgTable";
import apis from "@/apis";
import formatter from "@/utils/formatter";

const api = apis.accounts.activity_expense_monthly;

// 月份下拉後綴：offset 0=本月 1=上月 2=上上月，其餘不加後綴
const MONTH_SUFFIX_LANG = {
  0: "component.date.picker.month",
  1: "component.date.picker.lastMonth",
  2: "component.date.picker.beforeLastMonth",
};

export default {
  components: {
    TgLayout,
    TgTable,
  },
  data() {
    return {
      columns: [],
      proxy: api.getData,
      searchData: {
        month: "",
        category: "",
        sort: "start_date_desc",
      },
      months: [],
      categories: [],
      summaryData: {
        apply_count_total: "0",
        expense_total: "0.00",
      },
    };
  },
  computed: {
    monthOptions() {
      return this.months.map((item) => {
        const monthKey = "column." + String(item.month).padStart(2, "0");
        const suffixKey = MONTH_SUFFIX_LANG[item.offset];
        const label = suffixKey
          ? `${this.$t(monthKey)}(${this.$t(suffixKey)})`
          : this.$t(monthKey);

        return { value: item.value, label };
      });
    },
    categoryOptions() {
      const all = {
        value: "",
        label: this.$t("component.dropdown.activity.All"),
      };
      return [all].concat(
        this.categories.map((item) => ({
          value: item.id,
          label: this.categoryLabel(item.name, item.lang, item.target_to),
        })),
      );
    },
    sortOptions() {
      return [
        {
          value: "start_date_desc",
          label: this.$t("component.dropdown.sort.activity_start_date_desc"),
        },
        {
          value: "expense_desc",
          label: this.$t("component.dropdown.sort.activity_expense_desc"),
        },
      ];
    },
  },
  created() {
    this.$store.commit("breadcrumb/clear");
    api.getColumns().then((response) => {
      if (response.retcode != 0) return;
      this.columns = response.data.columns;
      this.months = response.data.months || [];
      this.categories = response.data.categories || [];
      // 月度報表預設帶本月（月份清單第一項），不提供「全部」
      if (this.months.length) {
        this.searchData.month = this.months[0].value;
      }
      this.$nextTick(() => this.handleSearch());
    });
  },
  methods: {
    // 系統派彩類別是固定字串，站台語言包有 key 就翻譯；沒灌 key 的站顯示後端回傳的中文，
    // 不會把原始 key 露在畫面上（活動名稱等站台自訂資料的 lang 為空，一律走 fallback）
    translate(langKey, fallback) {
      return langKey && this.$te(langKey) ? this.$t(langKey) : fallback;
    },
    // 活動類別顯示為「類別名稱(活動對象)」：類別主檔存在同名但活動對象不同的資料
    // （如 充值优惠 分一般會員／合夥人兩筆），只顯示名稱會看到兩個一模一樣的選項。
    // 活動對象沿用活動管理頁既有的 enum.target_to[N]，系統派彩類別無此概念
    // （後端給空字串）則不加後綴。
    categoryLabel(name, langKey, targetTo) {
      const label = this.translate(langKey, name);

      if (!targetTo) return label;

      const targetKey = `enum.target_to[${targetTo}]`;

      return this.$te(targetKey) ? `${label}(${this.$t(targetKey)})` : label;
    },
    // TgTable 的 formatter：format('lang') 的欄位查同列的 {欄位}_lang，其餘沿用共用 formatter
    formatCell(row, column, value) {
      // 活動類別：後端另給結構化的 category_items（含活動對象），逐項組字串再串接；
      // 一個活動可掛多個類別，維持既有的 / 分隔
      if (column.name === "category" && Array.isArray(row.category_items) && row.category_items.length) {
        return row.category_items
          .map((item) => this.categoryLabel(item.name, item.lang, item.target_to))
          .join("/");
      }

      if (column.format === "lang") {
        return this.translate(row[column.name + "_lang"], value);
      }

      return formatter.format(value, column.format);
    },
    handleSearch() {
      this.$nextTick(() => {
        this.$refs.table.search({ ...this.searchData });
      });
    },
    handleLoaded(response) {
      if (response.retcode == 0 && response.summary) {
        this.summaryData = response.summary;
      }
    },
    // 合計列直接採用後端加總結果，避免前端浮點加總誤差
    parseSummary() {
      const sums = [];
      const totals = {
        apply_count: this.summaryData.apply_count_total,
        expense: this.summaryData.expense_total,
      };

      this.columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = this.$t("component.table.total");
          return;
        }

        if (column.name in totals) {
          sums[index] = column.format
            ? formatter.format(totals[column.name], column.format)
            : totals[column.name];
          return;
        }

        sums[index] = "";
      });

      return sums;
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-row {
  padding: 10px 5px;
  flex-wrap: wrap;
}

.filter-label {
  margin: 0 8px 0 16px;
  font-size: 13px;
  white-space: nowrap;

  &:first-child {
    margin-left: 0;
  }
}

.filter-select {
  width: 150px;

  // 活動類別加上「(活動對象)」後綴後字數較多，150px 會截斷（如 充值优惠(合伙人会员)）
  &--category {
    width: 230px;
  }

  &--wide {
    width: 260px;
  }
}
</style>
